<template>
    <div class="mt-4" v-if="shouldShowExecutorSelector">
        <b-alert show :variant="isReassignment ? 'warning' : 'info'" class="mb-3">
            <div class="d-flex align-items-center">
                <i class="fas mr-2" :class="isReassignment ? 'fa-exclamation-triangle' : 'fa-info-circle'"></i>
                <strong>{{ isReassignment ? 'Переназначение исполнителя' : 'Необходимо назначить исполнителя' }}</strong>
            </div>
        </b-alert>

        <div class="executor-selector-panel p-3 border rounded bg-light">
            <h6 class="mb-3">
                <i class="fas fa-user-plus"></i> {{ isReassignment ? 'Переназначение исполнителя' : 'Назначение исполнителя' }}
            </h6>
            <div class="row">
                <div class="col-md-6">
                    <b-form-group 
                        label="Выберите исполнителя:" 
                        label-for="executor-select"
                        :invalid-feedback="'Поле обязательно к заполнению'" 
                        :state="executorSelectState"
                    >
                        <v-select 
                            id="executor-select" 
                            v-model="selectedExecutorModel"
                            :options="executorOptions" 
                            label="text" 
                            :reduce="option => option.value"
                            :disabled="assigningExecutor" 
                            placeholder="Начните вводить имя исполнителя..."
                            :clearable="true" 
                            :class="executorSelectState === false ? 'is-invalid' : ''" 
                        />
                        <EmployeeSelectionPreview :user-id="selectedExecutorModel" :options="executorOptions" />
                    </b-form-group>
                    <small class="form-text text-muted mt-1">
                        <i class="fas fa-lightbulb"></i>
                        {{ isReassignment ? 'Текущий исполнитель будет заменен на выбранного.' : 'Исполнители отсортированы по приоритету. Первый в списке - основной исполнитель.' }}
                    </small>
                </div>
                <div class="col-md-6">
                    <b-button 
                        variant="outline-primary" 
                        size="sm" 
                        @click="assignExecutor" 
                        style="margin-top: 29px;" 
                        v-if="canAssign"
                        :disabled="!selectedExecutorId || assigningExecutor"
                    >
                        <template v-if="assigningExecutor">
                            <b-spinner small class="mr-1"></b-spinner>
                            Назначение...
                        </template>
                        <template v-else>
                            <i class="fas fa-user-plus"></i> {{ isReassignment ? 'Переназначить исполнителя' : 'Назначить исполнителя' }}
                        </template>
                    </b-button> 
                </div>
            </div>
        </div>

        <ClosingActionModal
            ref="assignClosingModal"
            v-model="showAssignClosingModal"
            :loading="assigningExecutor"
            :required="closingCommentRequired"
            title="Назначение исполнителя"
            alert-text="Назначение переведёт заявку непосредственно в закрывающий статус."
            comment-label="Комментарий к назначению"
            placeholder="Добавьте пояснение к назначению…"
            confirm-text="Назначить исполнителя"
            loading-text="Назначение…"
            variant="primary"
            @confirm="handleClosingAssignConfirm"
        />
    </div>
</template>

<script>
import EmployeeSelectionPreview from '@/components/EmployeeSelectionPreview.vue';
import { mapState, mapGetters, mapActions } from 'vuex';
import ClosingActionModal from './modals/ClosingActionModal.vue';
import { actionClosesRequest, isRequestActionCommentRequired } from '@/utils/requestClosingComment';

/**
 * Автономный компонент селектора исполнителя
 * Сам подключается к store и выполняет назначение исполнителя
 */
export default {
    name: 'ExecutorSelector',
    components: {EmployeeSelectionPreview, ClosingActionModal },
    data() {
        return {
            showAssignClosingModal: false
        };
    },
    computed: {
        ...mapState('requestCard', [
            'request',
            'selectedExecutorId',
            'assigningExecutor'
        ]),
        ...mapGetters('requestCard', [
            'shouldShowExecutorSelector',
            'isReassignment',
            'executorOptions',
            'canAssign'
        ]),
        selectedExecutorModel: {
            get() {
                return this.selectedExecutorId;
            },
            set(val) {
                this.$store.commit('requestCard/setSelectedExecutorId', val);
            }
        },
        executorSelectState() {
            if (this.canAssign && !this.selectedExecutorId) {
                return false;
            }
            return null;
        },
        assignClosesRequest() {
            return actionClosesRequest(this.request, 'assign');
        },
        closingCommentRequired() {
            return isRequestActionCommentRequired(this.request, 'assign');
        }
    },
    methods: {
        ...mapActions('requestCard', [
            'requestCard_assignExecutor'
        ]),

        async assignExecutor() {
            if (!this.selectedExecutorId) {
                this.$bvToast && this.$bvToast.toast('Выберите исполнителя из списка', { 
                    title: 'Ошибка', 
                    variant: 'warning', 
                    solid: true 
                });
                return;
            }

            if (this.assignClosesRequest || this.closingCommentRequired) {
                this.showAssignClosingModal = true;
                return;
            }

            const confirmed = await this.$bvModal.msgBoxConfirm(
                'Вы уверены, что хотите назначить исполнителя на эту заявку?',
                { contentClass: 'argus-glass-dialog',
                    title: 'Назначение исполнителя',
                    size: 'sm',
                    buttonSize: 'sm',
                    okVariant: 'primary',
                    okTitle: 'Да, назначить',
                    cancelTitle: 'Отмена',
                    footerClass: 'p-2',
                    hideHeaderClose: false,
                    centered: true
                }
            );

            if (!confirmed) return;

            await this.performAssignment(null);
        },

        async handleClosingAssignConfirm(comment) {
            const succeeded = await this.performAssignment(comment);
            if (!succeeded) return;
            this.showAssignClosingModal = false;
            if (this.$refs.assignClosingModal) this.$refs.assignClosingModal.reset();
        },

        async performAssignment(comment) {
            try {
                await this.requestCard_assignExecutor({ executorId: this.selectedExecutorId, comment });
                this.$bvToast && this.$bvToast.toast('Исполнитель назначен', { 
                    title: 'Успех', 
                    variant: 'success', 
                    solid: true 
                });
                return true;
            } catch (error) {
                const errorMsg = error.response && error.response.data && error.response.data.message
                    ? error.response.data.message
                    : 'Не удалось назначить исполнителя';
                this.$bvToast && this.$bvToast.toast(errorMsg, { 
                    title: 'Ошибка', 
                    variant: 'danger', 
                    solid: true 
                });
                return false;
            }
        }
    }
};
</script>

<style scoped>
.executor-selector-panel {
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
}

.executor-selector-panel h6 {
    color: #495057;
    font-weight: 600;
}

:deep(.v-select.is-invalid .vs__dropdown-toggle) {
    border-color: #dc3545;
}

:deep(.v-select.is-invalid .vs__dropdown-toggle:focus) {
    border-color: #dc3545;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
</style>
